import { useState, useMemo } from "react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { api } from "@convex/_generated/api"
import type { Id } from "@convex/_generated/dataModel"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Users, ChevronRight } from "lucide-react"
import { cn } from "@/lib/utils"
import { EmptyState } from "./-components"

export const Route = createFileRoute("/(app)/app/results/teams")({
  component: TeamsResultsPage,
})

function TeamsResultsPage() {
  const search = Route.useSearch()
  const navigate = useNavigate()

  const selectedWeek = search.week

  // Get overall leaderboard
  const overallData = useQuery(api.quizzes.handlers.getPublicTeamLeaderboard, {
    teamType: "teams",
  })

  // Get list of games for tabs
  const games = useQuery(api.quizzes.handlers.getPublishedQuizzesForType, {
    quizType: "teams",
  })

  // Get breakdown for selected game
  const gameBreakdown = useQuery(
    api.quizzes.handlers.getTeamGameBreakdown,
    selectedWeek ? { quizId: selectedWeek as Id<"quizzes"> } : "skip"
  )

  const handleTabChange = (value: string) => {
    void navigate({
      to: "/app/results/teams",
      search: {
        ...search,
        week: value === "overall" ? undefined : value,
      },
    })
  }

  if (overallData === undefined || games === undefined) {
    return <Skeleton className="h-[400px] w-full" />
  }

  const activeTab = selectedWeek ?? "overall"

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="overall">Overall</TabsTrigger>
          {games.map((game) => (
            <TabsTrigger key={game._id} value={game._id}>
              Week {game.gameNumber}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Overall Tab */}
        <TabsContent value="overall" className="mt-6">
          <TeamsOverallLeaderboard data={overallData} />
        </TabsContent>

        {/* Week Tabs */}
        {games.map((game) => (
          <TabsContent key={game._id} value={game._id} className="mt-6">
            {selectedWeek === game._id ? (
              <TeamsWeekBreakdown
                data={gameBreakdown}
                weekNumber={game.gameNumber}
              />
            ) : null}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}

// Overall Leaderboard Component
interface OverallData {
  teams: {
    teamId: Id<"teams">
    teamName: string
    totalScore: number
    totalRawScore: number
    totalPoints: number | null
    best9: number | null
    gamesPlayed: number
    gameScores: {
      quizId: Id<"quizzes">
      score: number | null
      rank: number | null
      points: number | null
    }[]
    rank: number
  }[]
  quizzes: { _id: Id<"quizzes">; name: string; gameNumber: number }[]
}

function TeamsOverallLeaderboard({ data }: { data: OverallData }) {
  const [sortKey, setSortKey] = useState<"total" | "name">("total")
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc")

  const handleSort = (key: "total" | "name") => {
    if (sortKey === key) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"))
    } else {
      setSortKey(key)
      setSortDir(key === "name" ? "asc" : "desc")
    }
  }

  const sortedTeams = useMemo(() => {
    return [...data.teams].sort((a, b) => {
      if (sortKey === "name") {
        const cmp = a.teamName.localeCompare(b.teamName)
        return sortDir === "asc" ? cmp : -cmp
      }
      // Sort by best9 (total points)
      const aVal = a.best9 ?? 0
      const bVal = b.best9 ?? 0
      return sortDir === "desc" ? bVal - aVal : aVal - bVal
    })
  }, [data.teams, sortKey, sortDir])

  if (data.teams.length === 0) {
    return (
      <EmptyState
        title="Teams League Standings"
        icon={Users}
        heading="No Results Yet"
        description="No published teams quiz results found."
      />
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Teams League Standings</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead
                className="sticky left-0 bg-background z-10 min-w-[180px] cursor-pointer hover:bg-muted/50"
                onClick={() => handleSort("name")}
              >
                <span className="flex items-center gap-1">
                  Team {sortKey === "name" && (sortDir === "asc" ? "↑" : "↓")}
                </span>
              </TableHead>
              <TableHead
                className="text-right min-w-[90px] font-bold bg-amber-50/50 dark:bg-amber-900/10 cursor-pointer hover:bg-amber-100/50"
                onClick={() => handleSort("total")}
              >
                <span className="flex items-center justify-end gap-1">
                  Total {sortKey === "total" && (sortDir === "asc" ? "↑" : "↓")}
                </span>
              </TableHead>
              {data.quizzes.map((quiz) => (
                <TableHead
                  key={quiz._id}
                  className="text-center min-w-[60px]"
                >
                  #{quiz.gameNumber}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedTeams.map((team) => (
              <TableRow key={team.teamId}>
                <TableCell className="font-medium sticky left-0 bg-background z-10">
                  {team.teamName}
                </TableCell>
                <TableCell className="text-right font-bold tabular-nums bg-amber-50/50 dark:bg-amber-900/10">
                  {team.best9?.toLocaleString() ?? 0}
                </TableCell>
                {team.gameScores.map((game) => (
                  <TableCell
                    key={game.quizId}
                    className="text-center tabular-nums"
                    title={
                      game.score !== null
                        ? `Raw: ${game.score}${game.rank ? `, Rank: #${game.rank}` : ""}`
                        : undefined
                    }
                  >
                    {game.points !== null ? (
                      <span>{game.points}</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

// Week Breakdown Component with expandable rounds
interface WeekBreakdownData {
  quiz: { _id: Id<"quizzes">; name: string; totalRounds: number } | null
  teams: {
    teamId: Id<"teams">
    teamName: string
    questionScores: {
      questionId: Id<"questions">
      roundNumber: number
      order: number
      isCorrect: boolean
      score: number
    }[]
    totalScore: number
    rank: number
  }[]
  questions: {
    questionId: Id<"questions">
    roundNumber: number
    order: number
    text: string
  }[]
}

function TeamsWeekBreakdown({
  data,
  weekNumber,
}: {
  data: WeekBreakdownData | null | undefined
  weekNumber: number
}) {
  const [expandedRound, setExpandedRound] = useState<number | null>(null)
  const [sortKey, setSortKey] = useState<"total" | "points" | "name">("total")
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc")

  const toggleRound = (round: number) => {
    setExpandedRound((prev) => (prev === round ? null : round))
  }

  const handleSort = (key: "total" | "points" | "name") => {
    if (sortKey === key) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"))
    } else {
      setSortKey(key)
      setSortDir(key === "name" ? "asc" : "desc")
    }
  }

  if (data === undefined) {
    return <Skeleton className="h-[400px] w-full" />
  }

  if (!data?.quiz || data.teams.length === 0) {
    return (
      <EmptyState
        title={`Week ${weekNumber}`}
        icon={Users}
        heading="No Results"
        description="No teams results found for this week."
      />
    )
  }

  const totalRounds = data.quiz.totalRounds || 5
  const questionsPerRound = Math.ceil(data.questions.length / totalRounds)

  // Group questions by round
  const questionsByRound: Record<number, typeof data.questions> = {}
  for (let r = 1; r <= totalRounds; r++) {
    questionsByRound[r] = data.questions.filter((q) => q.roundNumber === r)
  }

  // Calculate round scores for each team
  const teamsWithRoundScores = data.teams.map((team) => {
    const roundScores: Record<number, number> = {}
    for (let r = 1; r <= totalRounds; r++) {
      roundScores[r] = team.questionScores
        .filter((q) => q.roundNumber === r)
        .reduce((sum, q) => sum + q.score, 0)
    }
    return { ...team, roundScores }
  })

  // Sort teams
  const sortedTeams = [...teamsWithRoundScores].sort((a, b) => {
    if (sortKey === "name") {
      const cmp = a.teamName.localeCompare(b.teamName)
      return sortDir === "asc" ? cmp : -cmp
    }
    const aVal = a.totalScore
    const bVal = b.totalScore
    return sortDir === "desc" ? bVal - aVal : aVal - bVal
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle>Week {weekNumber}</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead
                className="sticky left-0 bg-background z-10 min-w-[180px] cursor-pointer hover:bg-muted/50"
                onClick={() => handleSort("name")}
              >
                <span className="flex items-center gap-1">
                  Team {sortKey === "name" && (sortDir === "asc" ? "↑" : "↓")}
                </span>
              </TableHead>
              <TableHead
                className="text-right min-w-[70px] font-bold bg-amber-50/50 dark:bg-amber-900/10 cursor-pointer hover:bg-amber-100/50"
                onClick={() => handleSort("total")}
              >
                <span className="flex items-center justify-end gap-1">
                  Total {sortKey === "total" && (sortDir === "asc" ? "↑" : "↓")}
                </span>
              </TableHead>
              {/* Round headers */}
              {Array.from({ length: totalRounds }, (_, i) => {
                const roundNum = i + 1
                const isExpanded = expandedRound === roundNum
                return (
                  <TableHead
                    key={`round-${roundNum}`}
                    colSpan={isExpanded ? questionsPerRound + 1 : 1}
                    className={cn(
                      "text-center cursor-pointer hover:bg-muted/50 select-none",
                      isExpanded && "bg-muted/30"
                    )}
                    onClick={() => toggleRound(roundNum)}
                  >
                    <div className="flex items-center justify-center gap-1">
                      <ChevronRight
                        className={cn(
                          "h-3 w-3 transition-transform",
                          isExpanded && "rotate-90"
                        )}
                      />
                      R{roundNum}
                    </div>
                  </TableHead>
                )
              })}
            </TableRow>
            {/* Question number subheaders when a round is expanded */}
            {expandedRound !== null && (
              <TableRow>
                <TableHead className="sticky left-0 bg-background z-10" />
                <TableHead />
                {Array.from({ length: totalRounds }, (_, i) => {
                  const roundNum = i + 1
                  const isExpanded = expandedRound === roundNum
                  if (!isExpanded) {
                    return <TableHead key={`sub-${roundNum}`} />
                  }
                  const qStart = (roundNum - 1) * questionsPerRound + 1
                  return (
                    <>
                      <TableHead key={`sub-total-${roundNum}`} />
                      {Array.from({ length: questionsPerRound }, (_, qi) => (
                        <TableHead
                          key={`q-${roundNum}-${qi}`}
                          className="text-center text-xs font-medium text-muted-foreground min-w-[32px] px-1"
                        >
                          Q{qStart + qi}
                        </TableHead>
                      ))}
                    </>
                  )
                })}
              </TableRow>
            )}
          </TableHeader>
          <TableBody>
            {sortedTeams.map((team) => (
              <TableRow key={team.teamId}>
                <TableCell className="font-medium sticky left-0 bg-background z-10">
                  {team.teamName}
                </TableCell>
                <TableCell className="text-right font-bold tabular-nums bg-amber-50/50 dark:bg-amber-900/10">
                  {team.totalScore}
                </TableCell>
                {Array.from({ length: totalRounds }, (_, i) => {
                  const roundNum = i + 1
                  const isExpanded = expandedRound === roundNum
                  const roundScore = team.roundScores[roundNum]

                  if (!isExpanded) {
                    return (
                      <TableCell
                        key={`r-${roundNum}`}
                        className="text-center tabular-nums"
                      >
                        {roundScore}
                      </TableCell>
                    )
                  }

                  // Expanded: show round total + individual questions
                  const qStart = (roundNum - 1) * questionsPerRound
                  const roundQuestions = questionsByRound[roundNum] || []

                  return (
                    <>
                      <TableCell
                        key={`r-total-${roundNum}`}
                        className="text-center font-semibold tabular-nums bg-muted/20 border-l"
                      >
                        {roundScore}
                      </TableCell>
                      {Array.from({ length: questionsPerRound }, (_, qi) => {
                        const qNum = qStart + qi + 1
                        const questionScore = team.questionScores.find(
                          (q) =>
                            q.roundNumber === roundNum && q.order === qi
                        )
                        const question = roundQuestions[qi]

                        if (!questionScore) {
                          return (
                            <TableCell
                              key={`q-${roundNum}-${qi}`}
                              className="text-center text-muted-foreground px-1"
                            >
                              —
                            </TableCell>
                          )
                        }

                        return (
                          <Tooltip key={`q-${roundNum}-${qi}`}>
                            <TooltipTrigger asChild>
                              <TableCell
                                className={cn(
                                  "text-center text-xs tabular-nums cursor-help px-1",
                                  questionScore.isCorrect
                                    ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                                    : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                                )}
                              >
                                {questionScore.score}
                              </TableCell>
                            </TooltipTrigger>
                            {question?.text && (
                              <TooltipContent side="top" className="max-w-xs">
                                <p className="text-sm">{question.text}</p>
                              </TooltipContent>
                            )}
                          </Tooltip>
                        )
                      })}
                    </>
                  )
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}
